import _createForOfIteratorHelper from "@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
//
// Copyright 2021 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import TreeView from "@material-ui/lab/TreeView";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CircularProgress from "@material-ui/core/CircularProgress";
import Link from "@material-ui/core/Link";
import * as Utils from "../Utils";
import * as Config from "../configuration/utils/ConfigurationRegistry";
import ErrorMessageDialog, { useErrorMessageDialog } from "../configuration/dialog/ErrorMessageDialog";
import TreeListItem from "./TreeListItem";
import debounce from 'lodash.debounce';
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      margin: "auto"
    },
    highlighted: {
      backgroundColor: "yellow"
    },
    textField: {
      minWidth: "100%"
    },
    paper: {
      minWidth: "300px"
    },
    button: {
      margin: theme.spacing(0.5, 0)
    },
    allItem: {
      color: "red"
    },
    caption: {
      marginRight: "8px"
    },
    link: {
      cursor: "pointer"
    }
  };
});
function LoadedTreeList(props) {
  if (props.busy) {
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(CircularProgress, {
      color: "inherit",
      size: 20
    }));
  }
  if (props.options) {
    return /*#__PURE__*/React.createElement(TreeListItem, props);
  }
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(CircularProgress, {
    color: "inherit",
    size: 20
  }));
}
var getNodeIds = function getNodeIds(nodeIds, nodes) {
  if (nodes) {
    nodes.forEach(function (node) {
      nodeIds.push(node.id);
      getNodeIds(nodeIds, node.children);
    });
  }
};
export default function LoadedTreeView(_ref) {
  var defaultLabel = _ref.defaultLabel,
    forceLabel = _ref.forceLabel,
    path = _ref.path,
    url = _ref.url,
    handleEdit = _ref.handleEdit,
    editableNodeIdSet = _ref.editableNodeIdSet,
    radioTreeMode = _ref.radioTreeMode,
    startWithRootCollapsed = _ref.startWithRootCollapsed,
    storeFullNodes = _ref.storeFullNodes,
    allowRefresh = _ref.allowRefresh,
    selectedNodeIdsReceiver = _ref.selectedNodeIdsReceiver,
    _ref$showCheckBoxes = _ref.showCheckBoxes,
    showCheckBoxes = _ref$showCheckBoxes === void 0 ? true : _ref$showCheckBoxes;
  var classes = useStyles();

  //
  // Configuration entries 
  var config = path ? Config.fromPath(path, true) : null;
  var rootNodeId = config ? config.key : "";
  var sequentialNodeIdGenerator = 0;

  //
  // Primary effect
  React.useEffect(function () {
    console.log(">> TreeView, useEffect([])");
    if (config) {
      console.log(">> TreeView, useEffect([]), config: ");
      console.table(config);
    }
    if (config && config.data.value && Array.isArray(config.data.value)) {
      if (storeFullNodes) {
        setSelectedNodeIds(config.data.value.map(function (node) {
          return node.id;
        }));
      } else {
        setSelectedNodeIds(config.data.value);
      }
    }
  }, []);
  var initiallySelectedNodes = startWithRootCollapsed ? [] : [rootNodeId];
  var useLabel = forceLabel ? forceLabel : config ? config.data.label : defaultLabel;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    searching = _React$useState2[0],
    setSearching = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    fetching = _React$useState4[0],
    setFetching = _React$useState4[1];
  var _React$useState5 = React.useState(null),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    nodes = _React$useState6[0],
    setNodes = _React$useState6[1];
  var _React$useState7 = React.useState(null),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    filteredNodes = _React$useState8[0],
    setFilteredNodes = _React$useState8[1];
  var _React$useState9 = React.useState([]),
    _React$useState10 = _slicedToArray(_React$useState9, 2),
    allNodeIds = _React$useState10[0],
    setAllNodeIds = _React$useState10[1];
  var _React$useState11 = React.useState([]),
    _React$useState12 = _slicedToArray(_React$useState11, 2),
    selectedNodeIds = _React$useState12[0],
    setSelectedNodeIds = _React$useState12[1];
  var _React$useState13 = React.useState([]),
    _React$useState14 = _slicedToArray(_React$useState13, 2),
    debouncedSelectedNodeIds = _React$useState14[0],
    setDebouncedSelectedNodeIds = _React$useState14[1];
  var debouncedSelectedNodeIdsHandler = React.useCallback(debounce(setDebouncedSelectedNodeIds, 1000), []);
  var _React$useState15 = React.useState(initiallySelectedNodes),
    _React$useState16 = _slicedToArray(_React$useState15, 2),
    expandedNodeIds = _React$useState16[0],
    setExpandedNodeIds = _React$useState16[1];
  var _React$useState17 = React.useState(""),
    _React$useState18 = _slicedToArray(_React$useState17, 2),
    searchText = _React$useState18[0],
    setSearchText = _React$useState18[1];
  var _React$useState19 = React.useState(""),
    _React$useState20 = _slicedToArray(_React$useState19, 2),
    debouncedSearchText = _React$useState20[0],
    setDebouncedSearchText = _React$useState20[1];
  var debouncedChangeHandler = React.useCallback(debounce(setDebouncedSearchText, 1000), []);
  var _React$useState21 = React.useState([]),
    _React$useState22 = _slicedToArray(_React$useState21, 2),
    unselectedNodeIdsWithSelectedChildren = _React$useState22[0],
    setUnselectedNodeIdsWithSelectedChildren = _React$useState22[1];

  // noinspection DuplicatedCode
  var _useErrorMessageDialo = useErrorMessageDialog(),
    _useErrorMessageDialo2 = _slicedToArray(_useErrorMessageDialo, 2),
    errorMessage = _useErrorMessageDialo2[0],
    setErrorMessage = _useErrorMessageDialo2[1];

  //
  // Effect: URL changes
  //
  React.useEffect(function () {
    console.log(">> TreeView.React.useEffect(fetch)[url]");
    fetch();
  }, [url]);

  //
  // Effect: Search text (now debounced)
  //
  React.useEffect(function () {
    console.log(">> TreeView.React.useEffect(debouncedChangeHandler(searchText))[searchText]");
    debouncedChangeHandler(searchText);
  }, [searchText]);
  React.useEffect(function () {
    console.log(">> TreeView.React.useEffect(...)[debouncedSearchText]");
    var useFilteredNodeIds = initiallySelectedNodes;
    if (debouncedSearchText) {
      //
      setSearching(true);
      var searchTextLowerCase = debouncedSearchText.toLowerCase();
      var filterNodes = function filterNodes(lNodes) {
        return lNodes.map(function (node) {
          return _objectSpread({}, node);
        }) // Clone nodes
        .filter(function (node) {
          node.children = _toConsumableArray(filterNodes(node.children));
          if (node.label.toLowerCase().indexOf(searchTextLowerCase) >= 0 || node.children.length > 0) {
            useFilteredNodeIds.push(node.id);
            return true;
          }
          return false;
        });
      };
      var _filteredNodes = filterNodes(nodes);
      setFilteredNodes(_filteredNodes);
    } else {
      setFilteredNodes(null);
    }
    setExpandedNodeIds(useFilteredNodeIds);

    //
    setSearching(false);
  }, [debouncedSearchText]);

  //
  // Effect: Selected node IDs
  //
  React.useEffect(function () {
    console.log(">> TreeView.React.useEffect() [debouncedSelectedNodeIds}");
    console.log(debouncedSelectedNodeIds);
    if (selectedNodeIdsReceiver) {
      selectedNodeIdsReceiver(debouncedSelectedNodeIds);
    }
  }, [debouncedSelectedNodeIds]);
  React.useEffect(function () {
    console.log(">> TreeView.React.useEffect(debouncedSelectedNodeIdsHandler) [selectedNodeIds}");
    debouncedSelectedNodeIdsHandler(selectedNodeIds);
  }, [selectedNodeIds]);
  React.useEffect(function () {
    console.log(">> TreeView.React.useEffect(...)[nodes]");
    var nodeIds = [rootNodeId];
    getNodeIds(nodeIds, nodes);
    setAllNodeIds(nodeIds);
  }, [nodes]);
  React.useEffect(function () {
    console.log(">> TreeView.useEffect(calculateSelectedParentNodeIds(...)) [nodes]");
    var rootSelected = calculateSelectedParentNodeIds(nodes, unselectedNodeIdsWithSelectedChildren);
    if (rootSelected > 0 && !selectedNodeIds.includes(rootNodeId)) {
      unselectedNodeIdsWithSelectedChildren.push(rootNodeId);
      setUnselectedNodeIdsWithSelectedChildren(unselectedNodeIdsWithSelectedChildren);
    }
  }, [nodes]);
  var fillMissingNodeIds = function fillMissingNodeIds(nodes) {
    // console.log(">> TreeView.fillMissingNodeIds");

    nodes.forEach(function (node, index) {
      if (!node.id) {
        if (!sequentialNodeIdGenerator) {
          sequentialNodeIdGenerator = 1;
        }
        node.id = "RandomNodeId_" + sequentialNodeIdGenerator;
        sequentialNodeIdGenerator++;
      }
      fillMissingNodeIds(node.children);
    });
  };
  var handleExpand = function handleExpand(event, nodeIds) {
    console.log(">> TreeView.handleExpand");
    setExpandedNodeIds(nodeIds);
  };
  var handleSelect = function handleSelect(event, nodeIds) {
    console.log(">> TreeView.handleSelect");
    setSelectedNodeIds(nodeIds);
    if (path) {
      if (storeFullNodes) {
        if (nodes && nodeIds) {
          var paths = [];
          var _iterator = _createForOfIteratorHelper(nodes),
            _step;
          try {
            for (_iterator.s(); !(_step = _iterator.n()).done;) {
              var node = _step.value;
              assembleSelectedNodes(nodeIds, [], node, paths);
            }
          } catch (err) {
            _iterator.e(err);
          } finally {
            _iterator.f();
          }
          Config.setDataValue(path, JSON.stringify(paths));
        }
      } else {
        Config.setDataValue(path, nodeIds);
      }
    }
  };
  var handleRefresh = function handleRefresh() {
    console.log(">> TreeView.handleRefresh");
    fetch("refresh=true");
  };
  var assembleSelectedNodes = function assembleSelectedNodes(selectedNodeIds, thisPath, thisElement, paths) {
    console.log(">> TreeView.assembleSelectedNodes");
    var pathCopy = _toConsumableArray(thisPath);
    pathCopy.push(thisElement);
    if (selectedNodeIds.includes(thisElement.id)) {
      console.log("pushing pathCopy to the list " + JSON.stringify(pathCopy.map(function (item) {
        return item.id;
      })));
      paths.push(_toConsumableArray(pathCopy));
    } else {
      var _iterator2 = _createForOfIteratorHelper(thisElement.children),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var childElement = _step2.value;
          assembleSelectedNodes(selectedNodeIds, pathCopy, childElement, paths);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
    }
  };
  var fillLabelsFromNameAndDescription = function fillLabelsFromNameAndDescription(nodes) {
    // console.log(">> TreeView.fillLabelsFromNameAndDescription");

    nodes.forEach(function (node, index) {
      var label = "";
      if (node.name) {
        label = node.name;
      }
      if (node.description) {
        if (label) {
          label += " - " + node.description;
        } else {
          label = node.description;
        }
      }
      node.label = label;
      fillLabelsFromNameAndDescription(node.children);
    });
  };
  var fetch = function fetch(queryString) {
    console.log(">> TreeView.fetch");
    var useUrl = url;
    if (queryString) {
      useUrl = useUrl + "?" + queryString;
    }
    setNodes(null);
    setFetching(true);
    Utils.fetchData(useUrl).then(function (nodes) {
      fillMissingNodeIds(nodes);
      fillLabelsFromNameAndDescription(nodes);
      setNodes(nodes);
    }).catch(function (response) {
      var dataLabel = config ? config.data.label : "";
      Utils.getErrorMessage(response, dataLabel + " tree request failed. ").then(setErrorMessage);
      setNodes([]);
    }).finally(function () {
      setFetching(false);
    });
  };
  var handleClearAll = function handleClearAll() {
    console.log(">> TreeView.handleClearAll");
    setSelectedNodeIds([]);
  };
  var handleExpandAll = function handleExpandAll() {
    console.log(">> TreeView.handleExpandAll");
    setFilteredNodes(allNodeIds);
    setFilteredNodes(nodes);
    setExpandedNodeIds(allNodeIds);
  };
  var handleLoadDefaults = function handleLoadDefaults() {
    console.log(">> TreeView.handleLoadDefaults");
    setFilteredNodes(allNodeIds);
    setFilteredNodes(nodes);
    setExpandedNodeIds(allNodeIds);
  };
  var onSearchChange = function onSearchChange(event) {
    console.log(">> TreeView.onSearchChange(" + event.target.value + ")");
    setSearchText(event.target.value);
  };
  var calculateSelectedParentNodeIds = function calculateSelectedParentNodeIds(lNodes, parentNodeIds) {
    // console.log(">> TreeView.calculateSelectedParentNodeIds");

    if (!lNodes) {
      return 0;
    }
    return lNodes.reduce(function (count, node) {
      if (selectedNodeIds.includes(node.id)) {
        return count + 1;
      }
      var selectedChildrenCount = calculateSelectedParentNodeIds(node.children, parentNodeIds);
      if (selectedChildrenCount > 0) {
        parentNodeIds.push(node.id);
        return count + 1;
      }
      return count;
    }, 0);
  };
  return /*#__PURE__*/React.createElement(TreeView, {
    className: classes.root,
    defaultCollapseIcon: /*#__PURE__*/React.createElement(ExpandMoreIcon, null),
    defaultExpandIcon: /*#__PURE__*/React.createElement(ChevronRightIcon, null),
    onNodeToggle: handleExpand,
    selected: [],
    expanded: expandedNodeIds,
    showCheckBoxes: showCheckBoxes
  }, /*#__PURE__*/React.createElement(ErrorMessageDialog, errorMessage), /*#__PURE__*/React.createElement(TextField, {
    size: "medium",
    label: "Search...",
    type: "text",
    value: searchText,
    onChange: onSearchChange,
    helperText: /*#__PURE__*/React.createElement("span", null, allowRefresh && /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "textSecondary",
      className: classes.caption
    }, /*#__PURE__*/React.createElement(Link, {
      className: classes.link,
      color: "inherit",
      onClick: handleRefresh
    }, "Refresh")), /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "textSecondary",
      className: classes.caption
    }, /*#__PURE__*/React.createElement(Link, {
      className: classes.link,
      color: "inherit",
      onClick: handleExpandAll
    }, "Expand all")), /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "textSecondary",
      className: classes.caption
    }, /*#__PURE__*/React.createElement(Link, {
      className: classes.link,
      color: "inherit",
      onClick: handleClearAll
    }, "Clear all")))
  }), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(LoadedTreeList, {
    classes: classes,
    label: useLabel,
    options: filteredNodes ? filteredNodes : nodes,
    nodeId: rootNodeId,
    selectedNodeIds: selectedNodeIds,
    storeFullNodes: true,
    expandedNodeIds: expandedNodeIds,
    halfSelectedNodeIds: unselectedNodeIdsWithSelectedChildren,
    handleSelect: handleSelect,
    highlightText: debouncedSearchText ? debouncedSearchText.toLowerCase() : null,
    isRootNode: true,
    handleEdit: handleEdit,
    allowRefresh: allowRefresh,
    editableNodeIdSet: editableNodeIdSet,
    radioTreeMode: radioTreeMode,
    showCheckBoxes: showCheckBoxes,
    busy: searching || fetching
  }));
}